// Core React Imports
import React from "react";

// Local Imports
import { AlertDialogBackground, DivColumn, DivRow, Input, ParagraphText } from "../../../styles/SharedStyles";
import ConstantsImages from "../../../constants/ConstantsImages";
import ConstantsTexts from "../../../constants/ConstantsTexts";
import WidgetIconFive from "../../icons/widget-icon-five/WidgetIconFive";
import useWidgetAlertDialogUpdateModelFields from "./useWidgetAlertDialogUpdateModelFields";
import WidgetIconFour from "../../icons/widget-icon-four/WidgetIconFour";

// CSS/Stylesheets
import "./WidgetAlertDialogUpdateModelFields.css";

const WidgetAlertDialogUpdateModelFields = ({ onClose }) => {
  const {
    selectedFieldType,
    newFieldText,
    ITEM_MODES,
    FIELD_CONFIG,
    handleFieldTypeSelect,
    getTotalItems,
    getSelectedFieldOptions,
    handleEnterEditMode,
    handleCancelEditMode,
    handleSaveEdit,
    handleInputChange,
    handleNewFieldInputChange,
    handleAddNewField,
    getItemMode,
    handleEnterDeleteMode,
    handleCancelDeleteMode,
    handleConfirmDelete,
    getEditedValue,
    isTextAlreadyExists,
  } = useWidgetAlertDialogUpdateModelFields();

  const renderHeader = () => {
    return (
      <DivRow className="alert-dialog-overlay-update-model-fields-2">
        <WidgetIconFive iconProp={ConstantsImages.SETTINGS_BLUE_IMAGE} widthIconProp="1.7rem" heightIconProp="1.7rem" />
        <ParagraphText className="alert-dialog-overlay-update-model-fields-text">{ConstantsTexts.CODE_226}</ParagraphText>
        <WidgetIconFour iconProp={ConstantsImages.CLOSE_2_IMAGE} widthIconProp="1.1rem" heightIconProp="1.1rem" handleOnClickProp={onClose} />
      </DivRow>
    );
  };

  const renderFieldHeaderItem = (text, fieldType) => {
    const isSelected = selectedFieldType === fieldType;

    return (
      <DivRow
        className="alert-dialog-overlay-update-model-fields-4"
        style={{ backgroundColor: isSelected ? "#2E2E2E" : "rgba(0, 0, 0, 0.05)" }}
        onClick={() => handleFieldTypeSelect(fieldType)}
      >
        <ParagraphText className="alert-dialog-overlay-update-model-fields-text-2" style={{ color: isSelected ? "#FFF" : "#2E2E2E", fontWeight: isSelected ? "900" : "700" }}>
          {text}
        </ParagraphText>
      </DivRow>
    );
  };

  const renderFieldsHeader = () => {
    return <DivRow className="alert-dialog-overlay-update-model-fields-3">{FIELD_CONFIG.map((field) => renderFieldHeaderItem(field.textKey, field.fieldType))}</DivRow>;
  };

  const renderCreateNewField = () => {
    const hasText = newFieldText && newFieldText.trim().length > 0;
    const isDuplicate = isTextAlreadyExists(newFieldText);
    const canSave = hasText && !isDuplicate;

    return (
      <DivColumn className="alert-dialog-overlay-update-model-fields-5">
        <DivRow className="alert-dialog-overlay-update-model-fields-6">
          <WidgetIconFive iconProp={ConstantsImages.SETTINGS_BLUE_IMAGE} widthIconProp="1.7rem" heightIconProp="1.7rem" />
          <DivColumn className="alert-dialog-overlay-update-model-fields-7">
            <Input
              placeholder={ConstantsTexts.CODE_227}
              style={{ color: "#5051A1", fontFamily: "Almoni DL AAA", fontSize: "1.4rem", fontWeight: "700" }}
              onChange={(e) => handleNewFieldInputChange(e.target.value)}
              value={newFieldText}
            />
          </DivColumn>
          <DivRow
            className="alert-dialog-overlay-update-model-fields-8"
            style={{ backgroundColor: canSave ? "#5971F6" : "#aec1ff", cursor: canSave ? "pointer" : "default" }}
            onClick={canSave ? handleAddNewField : undefined}
          >
            <WidgetIconFive iconProp={ConstantsImages.PLUS_WHITE_IMAGE} widthIconProp="1.3rem" heightIconProp="1.3rem" />
          </DivRow>
        </DivRow>
      </DivColumn>
    );
  };

  const renderFieldUnselectedItem = (option) => {
    return (
      <DivRow className="alert-dialog-overlay-update-model-fields-14">
        <DivRow className="alert-dialog-overlay-update-model-fields-11">
          <WidgetIconFour
            iconProp={ConstantsImages.PENCIL_IMAGE}
            widthIconProp="1.6rem"
            heightIconProp="1.6rem"
            handleOnClickProp={() => handleEnterEditMode(option.itemId, option.displayName)}
          />
          <ParagraphText className="alert-dialog-overlay-update-model-fields-text-3">{option.displayName}</ParagraphText>
        </DivRow>
      </DivRow>
    );
  };

  const renderFieldSelectedItem = (option) => {
    const editedValue = getEditedValue(option.itemId);
    const hasText = editedValue && editedValue.trim().length > 0;
    const isDuplicate = isTextAlreadyExists(editedValue, option.itemId);
    const canSave = hasText && !isDuplicate;

    return (
      <DivRow className="alert-dialog-overlay-update-model-fields-14">
        <DivRow className="alert-dialog-overlay-update-model-fields-12">
          <WidgetIconFour
            iconProp={ConstantsImages.PENCIL_CANCEL_IMAGE}
            widthIconProp="1.6rem"
            heightIconProp="1.6rem"
            handleOnClickProp={() => handleCancelEditMode(option.itemId)}
          />
          <Input
            value={editedValue}
            style={{ color: "#5971F6", fontFamily: "Almoni DL AAA", fontSize: "1.4rem", fontWeight: "700" }}
            onChange={(e) => handleInputChange(option.itemId, e.target.value)}
          />
          <DivRow
            className="alert-dialog-overlay-update-model-fields-13"
            style={{ backgroundColor: canSave ? "#5971F6" : "#aec1ff", cursor: canSave ? "pointer" : "default" }}
            onClick={canSave ? () => handleSaveEdit(option.itemId) : undefined}
          >
            <WidgetIconFive iconProp={ConstantsImages.CHECK_WHITE_2_IMAGE} widthIconProp="0.9rem" heightIconProp="0.9rem" />
          </DivRow>
        </DivRow>
        <WidgetIconFour iconProp={ConstantsImages.DELETE_2_IMAGE} widthIconProp="1.7rem" heightIconProp="1.8rem" handleOnClickProp={() => handleEnterDeleteMode(option.itemId)} />
      </DivRow>
    );
  };

  const renderFieldReadyToDeleteItem = (option) => {
    return (
      <DivColumn className="alert-dialog-overlay-update-model-fields-15">
        <DivRow className="alert-dialog-overlay-update-model-fields-16">
          <WidgetIconFive iconProp={ConstantsImages.DELETE_2_IMAGE} widthIconProp="1.7rem" heightIconProp="1.8rem" />
          <ParagraphText className="alert-dialog-overlay-update-model-fields-text-3">{option.displayName}</ParagraphText>
        </DivRow>
        <DivRow className="alert-dialog-overlay-update-model-fields-17">
          <WidgetIconFive iconProp={ConstantsImages.ALERT_IMAGE} widthIconProp="1.7rem" heightIconProp="1.7rem" />
          <ParagraphText className="alert-dialog-overlay-update-model-fields-text-4">{ConstantsTexts.CODE_233.replace("{count}", option.count)}</ParagraphText>
          <DivRow className="alert-dialog-overlay-update-model-fields-18" onClick={() => handleConfirmDelete(option.itemId)}>
            <ParagraphText className="alert-dialog-overlay-update-model-fields-text-5">{ConstantsTexts.CODE_207}</ParagraphText>
          </DivRow>
          <DivRow className="alert-dialog-overlay-update-model-fields-18" style={{ backgroundColor: "#727272" }} onClick={() => handleCancelDeleteMode(option.itemId)}>
            <ParagraphText className="alert-dialog-overlay-update-model-fields-text-5">{ConstantsTexts.CODE_7}</ParagraphText>
          </DivRow>
        </DivRow>
      </DivColumn>
    );
  };

  const renderFieldItem = (option) => {
    const itemMode = getItemMode(option.itemId);

    if (itemMode === ITEM_MODES.DELETE) {
      return <DivRow className="alert-dialog-overlay-update-model-fields-10">{renderFieldReadyToDeleteItem(option)}</DivRow>;
    }

    return (
      <DivRow className="alert-dialog-overlay-update-model-fields-10">{itemMode === ITEM_MODES.EDIT ? renderFieldSelectedItem(option) : renderFieldUnselectedItem(option)}</DivRow>
    );
  };

  const renderListOfFields = () => {
    const totalItems = getTotalItems();
    const selectedOptions = getSelectedFieldOptions();

    if (totalItems === 0) {
      return (
        <DivColumn className="alert-dialog-overlay-update-model-fields-9">
          <ParagraphText style={{ color: "#000", textAlign: "center" }}>{ConstantsTexts.CODE_232}</ParagraphText>
        </DivColumn>
      );
    }

    return (
      <DivColumn className="alert-dialog-overlay-update-model-fields-9">
        {selectedOptions.map((option, index) => React.cloneElement(renderFieldItem(option), { key: option.itemId }))}
      </DivColumn>
    );
  };

  return (
    <AlertDialogBackground>
      <DivColumn className="alert-dialog-overlay-update-model-fields">
        {renderHeader()}
        {renderFieldsHeader()}
        {renderCreateNewField()}
        {renderListOfFields()}
      </DivColumn>
    </AlertDialogBackground>
  );
};

export default WidgetAlertDialogUpdateModelFields;
